<section class="b2c-page">
  <header class="page-header">
    <div>
      <span class="eyebrow">SELF-SERVICE PATIENTS</span>
      <h1>B2C operations</h1>
      <p>Monitor referral performance, manage kit inventory, and control partner access.</p>
    </div>
    <div class="status-pill"><span></span> Live operations</div>
  </header>

  <nav class="tab-list" aria-label="B2C operation sections">
    <button *ngFor="let item of tabs" type="button" class="tab-button" [class.active]="tab === item"
            [attr.aria-current]="tab === item ? 'page' : null" (click)="tab = item">
      <span class="tab-icon">{{ tabIcon(item) }}</span>{{ item }}
    </button>
  </nav>

  <div *ngIf="error" class="alert" role="alert">
    <span>!</span><div><strong>Something went wrong</strong><p>{{ error }}</p></div>
  </div>

  <div *ngIf="tab === 'Funnel'" class="section-stack">
    <div class="filter-panel">
      <div class="filter-copy">
        <span class="panel-kicker">FILTER VIEW</span>
        <strong>Partner performance</strong>
        <p>Leave the Organization ID empty to show the global B2C funnel.</p>
      </div>
      <label class="field">
        <span>Organization ID</span>
        <div class="filter-row">
          <input [(ngModel)]="partnerId" placeholder="All partner Organizations" (keyup.enter)="loadFunnel()">
          <button type="button" class="primary-button" (click)="loadFunnel()">Apply filter</button>
          <button *ngIf="partnerId" type="button" class="ghost-button" (click)="clearPartnerFilter()">Clear</button>
        </div>
      </label>
    </div>

    <div class="metric-grid">
      <article class="metric-card" *ngFor="let metric of funnelKeys" [attr.data-tone]="metricTone(metric)">
        <div class="metric-heading">
          <span class="metric-icon">{{ metricIcon(metric) }}</span>
          <span class="metric-label">{{ label(metric) }}</span>
        </div>
        <div class="metric-value">{{ funnel[metric] || 0 }}<small *ngIf="metric === 'conversion_rate'">%</small></div>
        <div class="metric-foot">{{ metricDescription(metric) }}</div>
      </article>
    </div>
  </div>

  <div *ngIf="tab === 'Inventory'" class="section-stack">
    <div class="panel inventory-toolbar">
      <div>
        <span class="panel-kicker">KIT INVENTORY</span>
        <h2>Import issued kits</h2>
        <p>Upload a CSV with a <code>barcode</code> column. Duplicates are excluded before import.</p>
      </div>
      <div class="upload-actions">
        <label class="file-button"><input type="file" accept=".csv" (change)="previewCsv($event)">Choose CSV</label>
        <button type="button" class="primary-button" [disabled]="!kitPreview.length" (click)="importKits()">Import {{ kitPreview.length }} valid kits</button>
      </div>
    </div>
    <div *ngIf="duplicates.length" class="warning-note">Duplicates in file: {{ duplicates.join(', ') }}</div>
    <div *ngIf="importIssues" class="warning-note">
      <div *ngIf="importIssues.invalid.length">Invalid barcodes (must be 10–20 digits): {{ importIssues.invalid.join(', ') }}</div>
      <div *ngIf="importIssues.duplicates.length">Duplicated in file: {{ importIssues.duplicates.join(', ') }}</div>
      <div *ngIf="importIssues.existing.length">Already in inventory: {{ importIssues.existing.join(', ') }}</div>
    </div>

    <div class="panel inventory-toolbar">
      <div>
        <span class="panel-kicker">PARTNER ALLOCATION</span>
        <h2>Assign kits to a partner</h2>
        <p>Tick kits below or enter a barcode range. Attribution stays on the kit permanently once a customer registers it.</p>
      </div>
      <div class="upload-actions">
        <select [(ngModel)]="assignPartnerId">
          <option value="">Unassign (return to Glacies stock)</option>
          <option *ngFor="let org of organizations" [value]="org.id">{{ org.name }}</option>
        </select>
        <input type="text" placeholder="Range from" [(ngModel)]="rangeFrom">
        <input type="text" placeholder="Range to" [(ngModel)]="rangeTo">
        <input type="text" placeholder="Reason (required to reassign)" [(ngModel)]="assignReason">
        <button type="button" class="table-action" (click)="previewAssign()">Preview</button>
        <button type="button" class="primary-button" [disabled]="!assignPreview" (click)="applyAssign()">Apply</button>
      </div>
    </div>
    <div *ngIf="assignPreview" class="warning-note">
      <div *ngIf="assignPreview.matched !== undefined">Matched {{ assignPreview.matched }} · assign {{ assignPreview.assigned }} · reassign {{ assignPreview.reassigned }} · unchanged {{ assignPreview.unchanged }}</div>
      <div *ngIf="assignPreview.blocked?.length">Already registered under another partner: {{ assignPreview.blocked.join(', ') }}</div>
      <div *ngIf="assignPreview.missing?.length">Not in inventory: {{ assignPreview.missing.join(', ') }}</div>
    </div>

    <div class="table-panel">
      <div class="table-header">
        <div><span class="panel-kicker">CURRENT INVENTORY</span><h2>{{ kits.length }} kits · {{ selected.size }} selected</h2></div>
        <div class="upload-actions">
          <select [(ngModel)]="kitFilter.status" (change)="loadKits()">
            <option value="">All statuses</option>
            <option *ngFor="let status of allStatuses" [value]="status">{{ status }}</option>
          </select>
          <select [(ngModel)]="kitFilter.partner" (change)="loadKits()" [disabled]="kitFilter.unassigned">
            <option value="">All partners</option>
            <option *ngFor="let org of organizations" [value]="org.id">{{ org.name }}</option>
          </select>
          <label><input type="checkbox" [(ngModel)]="kitFilter.unassigned" (change)="loadKits()"> Unassigned only</label>
        </div>
      </div>
      <div class="table-scroll">
        <table>
          <thead><tr><th></th><th>Barcode</th><th>Partner</th><th>Status</th><th>Claimed</th><th class="align-right">Action</th></tr></thead>
          <tbody>
            <tr *ngFor="let kit of kits">
              <td><input type="checkbox" [checked]="selected.has(kit.barcode)" (change)="toggleKit(kit)"></td>
              <td class="mono">{{ kit.barcode }}</td>
              <td>{{ kit.assigned_partner_name || 'Unassigned' }}</td>
              <td><span class="state-badge" [class.disabled]="kit.status === 'disabled'">{{ kit.status }}</span></td>
              <td>{{ kit.claimed_at || 'Not claimed' }}</td>
              <td class="align-right">
                <button *ngIf="!isPatientOwned(kit)" type="button" class="table-action" (click)="setKitStatus(kit, kit.status === 'disabled' ? 'issued' : 'disabled')">{{ kit.status === 'disabled' ? 'Activate' : 'Disable' }}</button>
                <span *ngIf="isPatientOwned(kit)" class="state-badge">With patient</span>
              </td>
            </tr>
            <tr *ngIf="!kits.length"><td colspan="6" class="empty-state">No kits have been imported yet.</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <div *ngIf="tab === 'Patient access'" class="section-stack">
    <div class="table-panel">
      <div class="table-header">
        <div><span class="panel-kicker">PARTNER CARE ACCESS</span><h2>{{ accesses.length }} access grants</h2></div>
        <p>Revocation applies immediately across partner search, dashboards, and direct patient URLs.</p>
      </div>
      <div class="table-scroll">
        <table>
          <thead><tr><th>Patient</th><th>B2C Organization</th><th>Referrer</th><th>Acquired</th><th>Status</th><th class="align-right">Action</th></tr></thead>
          <tbody>
            <tr *ngFor="let access of accesses">
              <td><strong>{{ access.patient_name }}</strong></td>
              <td>{{ access.b2c_organization_name }}</td>
              <td>{{ access.referring_organization_name }}</td>
              <td>{{ access.acquired_at || '—' }}</td>
              <td><span class="state-badge" [class.disabled]="access.status !== 'active'">{{ access.status }}</span></td>
              <td class="align-right">
                <div style="display: inline-flex; gap: 8px; align-items: center;">
                  <button type="button" class="table-action" (click)="openPatientReports(access.patient_id || access.patient?.id)">Reports & Biome</button>
                  <button *ngIf="access.status === 'active'" type="button" class="danger-button" (click)="revoke(access)">Revoke access</button>
                </div>
              </td>
            </tr>
            <tr *ngIf="!accesses.length"><td colspan="6" class="empty-state">No partner access grants found.</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</section>
